/* ============================================================
   Gibutech Meridian — behaviour
   Deliberately dependency-free and defensive. Two rules learned the hard way
   while prototyping this design:
     1. IntersectionObserver does not fire reliably inside embedded viewers,
        so anything it drives carries a timeout or scroll fallback.
     2. Sticky offsets are measured at runtime, never assumed, because the
        header height changes with the announcement bar and the subnav.
   ============================================================ */
(function () {
  'use strict';

  var root = document.documentElement;

  /* ---------- sticky offsets ---------- */
  function measureChrome() {
    var header = document.querySelector('[data-sticky-header]');
    var subnav = document.querySelector('[data-sticky-subnav]');
    var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
    var s = subnav ? Math.round(subnav.getBoundingClientRect().height) : 0;
    root.style.setProperty('--header-h', h + 'px');
    root.style.setProperty('--subnav-h', s + 'px');
  }
  measureChrome();
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(measureChrome);
    document.querySelectorAll('[data-sticky-header],[data-sticky-subnav]')
      .forEach(function (el) { ro.observe(el); });
  } else {
    window.addEventListener('resize', measureChrome);
  }
  window.addEventListener('load', measureChrome);

  /* ---------- scroll reveal ---------- */
  function revealAll() {
    document.querySelectorAll('.rv').forEach(function (el) { el.classList.add('is-in'); });
  }
  function initReveal() {
    if (!root.classList.contains('reveal-on')) { revealAll(); return; }
    if (!('IntersectionObserver' in window)) { revealAll(); return; }

    var pending = [].slice.call(document.querySelectorAll('.rv:not(.is-in)'));
    if (!pending.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { if (en.isIntersecting) show(en.target); });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });
    pending.forEach(function (el) { io.observe(el); });

    function show(el) {
      el.classList.add('is-in');
      io.unobserve(el);
      var i = pending.indexOf(el);
      if (i > -1) pending.splice(i, 1);
      if (!pending.length) window.removeEventListener('scroll', onScroll);
    }

    /* The safety net is per element and only ever does what the observer was
       supposed to do: reveal something that is actually in view. It used to be
       two blanket triggers - revealAll 1.6s after load, and revealAll again on
       the first scroll event - and between them they defeated the feature they
       were protecting. Measured on the home page (29 Sep): all 8 sections
       finished their animation within 1.1s while the visitor was still at the
       top, and every one of them was below the fold, so the reveal was never
       seen doing anything and scrolling the page produced no motion at all.
       Nothing off screen is touched now, so each section still has its entrance
       left to make when you reach it. */
    var ticking = false;
    function sweep() {
      ticking = false;
      for (var i = pending.length - 1; i >= 0; i--) {
        var r = pending[i].getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) show(pending[i]);
      }
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(sweep); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    /* One sweep once the page has settled, for anything already in view that
       the observer missed. */
    setTimeout(sweep, 1200);
  }
  initReveal();
  document.addEventListener('shopify:section:load', initReveal);

  /* ---------- horizontal rails ---------- */
  function wireRail(rail) {
    var track = rail.querySelector('.rail__track');
    if (!track) return;
    var prev = rail.querySelector('.rail__btn--prev');
    var next = rail.querySelector('.rail__btn--next');

    function step(dir) {
      // Script-driven smooth scrolling ignores the CSS reduced-motion rule, so ask here.
      var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: dir * Math.round(track.clientWidth * 0.8), behavior: calm ? 'auto' : 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });

    function syncArrows() {
      var max = track.scrollWidth - track.clientWidth;
      if (prev) prev.hidden = track.scrollLeft <= 2;
      if (next) next.hidden = track.scrollLeft >= max - 2;
    }
    track.addEventListener('scroll', syncArrows, { passive: true });
    window.addEventListener('resize', syncArrows);
    syncArrows();
  }
  function initRails(scope) {
    (scope || document).querySelectorAll('.rail').forEach(wireRail);
  }
  initRails();
  document.addEventListener('shopify:section:load', function (e) { initRails(e.target); });

  /* ---------- flyouts: mega menus, search, bag ----------
     Product menus open on hover like Apple's. Search and bag (data-mega-click)
     open on click and stay open while in use. Every flyout raises the curtain,
     and Escape, the curtain or a click elsewhere closes it. */
  var flyout = { open: function () {}, close: function () {} };
  window.Meridian = window.Meridian || {};
  window.Meridian.openPanel = function (key) { flyout.open(key, true); };
  window.Meridian.closePanels = function () { flyout.close(); };

  function initMega(scope) {
    var shell = (scope || document).querySelector('[data-nav-shell]') || document.querySelector('[data-nav-shell]');
    if (!shell) return;
    var panels = shell.querySelectorAll('[data-mega-panel]');
    var triggers = shell.querySelectorAll('[data-mega-trigger]');
    var curtain = document.querySelector('[data-curtain]');
    var closeTimer = null;
    var current = null;
    var lastTrigger = null;

    function panelFor(key) { return shell.querySelector('[data-mega-panel="' + key + '"]'); }

    function close(restoreFocus) {
      var panel = current && panelFor(current);
      var wasUtil = panel && panel.hasAttribute('data-util');
      var focusInside = panel && panel.contains(document.activeElement);
      panels.forEach(function (p) { p.dataset.open = 'false'; });
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      document.documentElement.classList.remove('has-flyout');
      if (restoreFocus && lastTrigger && (wasUtil || focusInside)) lastTrigger.focus();
      current = null;
    }
    function open(key, fromCode) {
      clearTimeout(closeTimer);
      var panel = panelFor(key);
      if (!panel) { close(); return; }
      /* The nav scrolls away with the page, the way Apple's global nav does, so
         a bag opened by a quick add halfway down the page would sit off-screen.
         Bring the header back first. Instant, not smooth: smooth scrolling is
         paused in some embedded viewers and the panel would open unseen. */
      if (panel.hasAttribute('data-util') && shell.getBoundingClientRect().top < 0) {
        window.scrollTo(0, 0);
      }
      panels.forEach(function (p) { p.dataset.open = String(p === panel); });
      triggers.forEach(function (t) {
        var match = t.dataset.megaTrigger === key;
        t.setAttribute('aria-expanded', String(match));
        if (match) lastTrigger = t;
      });
      document.documentElement.classList.add('has-flyout');
      current = key;
      if (panel.hasAttribute('data-util')) {
        var focusTarget = panel.querySelector('[data-search-input]') ||
                          (fromCode ? null : panel.querySelector('a, button, input, textarea'));
        if (focusTarget) setTimeout(function () { focusTarget.focus({ preventScroll: true }); }, 60);
      }
    }
    flyout.open = open;
    flyout.close = close;

    triggers.forEach(function (t) {
      var key = t.dataset.megaTrigger;
      var clickOnly = t.hasAttribute('data-mega-click');
      if (!clickOnly) {
        t.addEventListener('mouseenter', function () {
          // A hover menu never replaces a search or bag the customer opened.
          if (current && panelFor(current).hasAttribute('data-util')) return;
          open(key);
        });
      }
      t.addEventListener('click', function (e) {
        var panel = panelFor(key);
        if (!panel) return;
        /* Apple's top-level links go to their page when clicked; the flyout opens
           on hover. From the keyboard (a click with no pointer detail) Enter opens
           the flyout instead and moves focus to its first link. */
        if (!clickOnly && e.detail > 0) return;
        e.preventDefault();
        if (current === key) { close(true); return; }
        open(key);
        if (!clickOnly) {
          var first = panel.querySelector('a, button');
          if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, 30);
        }
      });
    });

    /* Tabbing past either end of an open product flyout returns to the bar:
       back to its own link, or on to the next one. */
    panels.forEach(function (panel) {
      if (panel.hasAttribute('data-util')) return;
      panel.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var links = panel.querySelectorAll('a, button');
        if (!links.length) return;
        var trigger = shell.querySelector('[data-mega-trigger="' + panel.dataset.megaPanel + '"]');
        var list = Array.prototype.slice.call(shell.querySelectorAll('.gn__item, .gn__menu'));
        if (!e.shiftKey && document.activeElement === links[links.length - 1]) {
          e.preventDefault();
          close();
          var next = list[list.indexOf(trigger) + 1];
          (next || trigger).focus();
        } else if (e.shiftKey && document.activeElement === links[0]) {
          e.preventDefault();
          close();
          trigger.focus();
        }
      });
    });
    shell.querySelectorAll('[data-nav-plain]').forEach(function (a) {
      a.addEventListener('mouseenter', function () {
        if (current && !panelFor(current).hasAttribute('data-util')) close();
      });
    });
    shell.addEventListener('mouseleave', function () {
      if (current && panelFor(current).hasAttribute('data-util')) return;
      closeTimer = setTimeout(close, 180);
    });
    shell.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
    if (curtain) curtain.addEventListener('click', function () { close(true); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && current) close(true); });
    document.addEventListener('click', function (e) {
      if (current && !shell.contains(e.target) && !e.target.closest('[data-sheet]')) close();
    });
  }
  initMega();
  document.addEventListener('shopify:section:load', function (e) { initMega(e.target); });

  /* ---------- phone menu ----------
     Apple's pattern: full screen, drill-down panels, a back chevron. The page
     behind is locked, focus stays inside, Escape steps back then closes. */
  function initMobileNav() {
    var menu = document.querySelector('[data-mnav]');
    var opener = document.querySelector('[data-mnav-open]');
    if (!menu || !opener || menu.dataset.wired) return;
    menu.dataset.wired = '1';
    var back = menu.querySelector('[data-mnav-back]');
    var closeBtn = menu.querySelector('[data-mnav-close]');
    var stage = menu.querySelector('.mnav__stage');

    function current() { return menu.querySelector('[data-mnav-panel]:not([hidden])'); }
    function show(key, focusEl) {
      menu.querySelectorAll('[data-mnav-panel]').forEach(function (p) {
        p.hidden = p.getAttribute('data-mnav-panel') !== key;
      });
      back.hidden = key === 'root';
      stage.scrollTop = 0;
      var target = focusEl || current().querySelector('a, button');
      if (target) target.focus({ preventScroll: true });
    }
    function open() {
      if (window.Meridian && window.Meridian.closePanels) window.Meridian.closePanels();
      menu.hidden = false;
      menu.dataset.open = 'true';
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('mnav-open');
      show('root');
    }
    function close() {
      menu.dataset.open = 'false';
      menu.hidden = true;
      opener.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('mnav-open');
      opener.focus({ preventScroll: true });
    }
    function stepBack() {
      var from = current().getAttribute('data-mnav-panel');
      show('root', menu.querySelector('[data-mnav-go="' + from + '"]'));
    }

    opener.addEventListener('click', open);
    closeBtn.addEventListener('click', close);
    back.addEventListener('click', stepBack);
    menu.addEventListener('click', function (e) {
      var go = e.target.closest('[data-mnav-go]');
      if (go) show(go.getAttribute('data-mnav-go'));
    });
    menu.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        back.hidden ? close() : stepBack();
        return;
      }
      if (e.key !== 'Tab') return;
      var focusable = Array.prototype.filter.call(
        menu.querySelectorAll('a[href], button'),
        function (el) { return !el.hidden && el.offsetParent !== null; }
      );
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Turning a tablet to landscape past the breakpoint should not strand the menu open.
    var wide = window.matchMedia('(min-width: 834px)');
    var onWide = function (e) { if (e.matches && menu.dataset.open === 'true') close(); };
    if (wide.addEventListener) wide.addEventListener('change', onWide); else wide.addListener(onWide);
  }
  initMobileNav();

  /* ---------- tabbed panels (curated grid) ----------
     Apple's pill tabs. Arrow keys move between tabs; a link to #panel-id opens
     that panel, so the nav can point straight at, say, iPhone 16 cases. */
  function initTabs(scope) {
    (scope || document).querySelectorAll('[data-tabs]').forEach(function (box) {
      if (box.dataset.wired) return;
      box.dataset.wired = '1';
      var tabs = Array.prototype.slice.call(box.querySelectorAll('[data-tab]'));
      var panels = Array.prototype.slice.call(box.querySelectorAll('[data-tab-panel]'));
      if (tabs.length < 2) return;
      box.classList.add('is-ready');
      function select(i, focus) {
        tabs.forEach(function (t, n) {
          var on = n === i;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          panels[n].hidden = !on;
        });
        if (focus) tabs[i].focus();
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { select(i); });
        t.addEventListener('keydown', function (e) {
          var n = null;
          if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') n = 0;
          if (e.key === 'End') n = tabs.length - 1;
          if (n !== null) { e.preventDefault(); select(n, true); }
        });
      });
      function fromHash() {
        var id = decodeURIComponent(location.hash.slice(1));
        var i = panels.findIndex(function (p) { return p.id === id; });
        if (i < 0) return false;
        select(i);
        box.scrollIntoView({ block: 'start' });
        return true;
      }
      if (!fromHash()) select(0);
      window.addEventListener('hashchange', fromHash);
    });
  }
  initTabs();
  document.addEventListener('shopify:section:load', function (e) { initTabs(e.target); });

  /* ---------- footer directory: Apple's phone accordion (833px and below) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ftd-toggle]');
    if (!btn) return;
    var open = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', String(!open));
  });
  document.addEventListener('shopify:section:load', initMobileNav);

  /* ---------- gallery ----------
     The main image is served with a srcset, and a srcset beats src: leaving the
     old one in place meant a thumbnail click highlighted the thumb and changed
     nothing on screen (23 Sep). Every switch now writes both, clearing srcset
     when the new picture has none. */
  function showMedia(gal, src, srcset, alt) {
    var main = gal.querySelector('[data-gallery-main]');
    if (!main || !src) return;
    main.srcset = srcset || '';
    main.src = src;
    if (alt !== undefined && alt !== null) main.alt = alt;
  }
  window.MeridianGallery = { show: showMedia };

  function initGallery(scope) {
    (scope || document).querySelectorAll('[data-gallery]').forEach(function (gal) {
      var main = gal.querySelector('[data-gallery-main]');
      if (!main) return;
      gal.querySelectorAll('[data-gallery-thumb]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          gal.querySelectorAll('[data-gallery-thumb]').forEach(function (o) {
            o.removeAttribute('aria-current');
          });
          btn.setAttribute('aria-current', 'true');
          showMedia(gal, btn.dataset.src, btn.dataset.srcset, btn.dataset.alt);
        });
      });

      /* The paddles drive the same thumbnails, so there is one code path for
         every image change and the strip stays in step with the big picture.
         It wraps, the way the rails do when you reach the end. */
      function step(dir) {
        var thumbs = [].slice.call(gal.querySelectorAll('[data-gallery-thumb]'));
        if (!thumbs.length) return;
        var at = thumbs.findIndex(function (t) { return t.getAttribute('aria-current') === 'true'; });
        if (at < 0) at = 0;
        thumbs[(at + dir + thumbs.length) % thumbs.length].click();
      }

      gal.querySelectorAll('[data-gallery-step]').forEach(function (paddle) {
        paddle.addEventListener('click', function () { step(parseInt(paddle.dataset.galleryStep, 10)); });
      });

      /* The paddles are hidden below 700px, where the strip becomes dots, so a
         phone gets the gesture it expects instead: a swipe across the picture.
         Only a clearly horizontal drag counts, so scrolling the page through
         the gallery still works. */
      var stage = gal.querySelector('.viewer__main');
      if (stage && window.matchMedia('(hover: none)').matches) {
        var x0 = null, y0 = null;
        stage.addEventListener('touchstart', function (e) {
          if (e.touches.length !== 1) { x0 = null; return; }
          x0 = e.touches[0].clientX; y0 = e.touches[0].clientY;
        }, { passive: true });
        stage.addEventListener('touchend', function (e) {
          if (x0 === null) return;
          var t = e.changedTouches[0];
          var dx = t.clientX - x0, dy = t.clientY - y0;
          x0 = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) step(dx < 0 ? 1 : -1);
        }, { passive: true });
      }
    });
  }
  initGallery();
  document.addEventListener('shopify:section:load', function (e) { initGallery(e.target); });

  /* ---------- sticky buy bar ----------
     Scroll driven, not IntersectionObserver, for the reason noted at the top. */
  function initBuyBar() {
    var bar = document.querySelector('[data-buy-bar]');
    var anchor = document.querySelector('[data-buy-anchor]');
    if (!bar || !anchor || bar.dataset.wired) return;
    bar.dataset.wired = '1';
    var ticking = false;
    /* The bar ships hidden so it never appears without JS. Nothing used to take
       that attribute off again, so the bar could only ever be invisible (23 Sep):
       data-show flipped, [hidden] kept display:none winning. Now the attribute
       comes off just before the slide up and goes back on once the slide down
       has finished, which also keeps its link out of the tab order while away. */
    function update() {
      ticking = false;
      var show = anchor.getBoundingClientRect().bottom < 90;
      if (show === (bar.dataset.show === 'true')) return;
      if (show) {
        bar.hidden = false;
        void bar.offsetHeight; // register the shown box so the transform animates
      }
      bar.dataset.show = show ? 'true' : 'false';
    }
    bar.addEventListener('transitionend', function (e) {
      if (e.propertyName === 'transform' && bar.dataset.show !== 'true') bar.hidden = true;
    });
    /* The bar's button submits the buy form, so commerce.js adds the chosen
       variant and quantity and opens the bag, exactly as the main button does. */
    var add = bar.querySelector('[data-buybar-add]');
    if (add) {
      add.addEventListener('click', function () {
        var form = document.querySelector('[data-product-form]');
        if (!form) return;
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }
  /* ---------- phone filter sheet ---------- */
  function initFacetSheet() {
    var sheet = document.querySelector('[data-facets-sheet]');
    var opener = document.querySelector('[data-facets-open]');
    if (!sheet || !opener || sheet.dataset.wired) return;
    sheet.dataset.wired = '1';
    opener.addEventListener('click', function () {
      if (window.Meridian && window.Meridian.closePanels) window.Meridian.closePanels();
      sheet.showModal();
    });
    sheet.querySelector('[data-facets-close]').addEventListener('click', function () { sheet.close(); });
    var sheetForm = sheet.querySelector('form');
    if (sheetForm) sheetForm.addEventListener('submit', function () { dropEmptyPrices(sheetForm); });
    // A click on the backdrop lands on the dialog element itself.
    sheet.addEventListener('click', function (e) { if (e.target === sheet) sheet.close(); });
    sheet.addEventListener('close', function () { opener.focus({ preventScroll: true }); });
  }
  initFacetSheet();

  /* Empty price fields would otherwise add "filter.v.price.gte=" to the address. */
  function dropEmptyPrices(form) {
    Array.prototype.forEach.call(form.querySelectorAll('input[type="number"]'), function (input) {
      if (input.value === '') input.disabled = true;
    });
  }

  /* ---------- desktop filter pills ---------- */
  var FBAR_KEY = 'meridian:fbar';
  function initFacetBar() {
    var bar = document.querySelector('[data-fbar]');
    if (!bar || bar.dataset.wired) return;
    bar.dataset.wired = '1';
    var groups = Array.prototype.slice.call(bar.querySelectorAll('[data-fbar-group]'));
    groups.forEach(function (g) {
      g.addEventListener('toggle', function () {
        if (!g.open) return;
        groups.forEach(function (o) { if (o !== g) o.open = false; });
      });
    });
    // Apple keeps the panel open after a choice; the page reloads here, so remember it.
    bar.addEventListener('change', function (e) {
      var g = e.target.closest('[data-fbar-group]');
      try { if (g) sessionStorage.setItem(FBAR_KEY, g.getAttribute('data-fbar-group')); } catch (err) {}
      dropEmptyPrices(bar);
    });
    try {
      var reopen = sessionStorage.getItem(FBAR_KEY);
      sessionStorage.removeItem(FBAR_KEY);
      var again = reopen && bar.querySelector('[data-fbar-group="' + reopen + '"]');
      if (again && window.matchMedia('(min-width: 735px)').matches) again.open = true;
    } catch (err) {}
    document.addEventListener('click', function (e) {
      if (!bar.contains(e.target)) groups.forEach(function (g) { g.open = false; });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      groups.forEach(function (g) {
        if (!g.open) return;
        g.open = false;
        g.querySelector('summary').focus({ preventScroll: true });
      });
    });
  }
  initFacetBar();
  document.addEventListener('shopify:section:load', initFacetBar);
  document.addEventListener('shopify:section:load', initFacetSheet);

  initBuyBar();
  document.addEventListener('shopify:section:load', initBuyBar);

  /* Share rows (articles and product pages): copy the page link. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-copy-link]');
    if (!btn || !navigator.clipboard) return;
    var status = btn.parentNode.querySelector('[data-copy-status]');
    navigator.clipboard.writeText(btn.getAttribute('data-copy-link')).then(function () {
      if (!status) return;
      status.textContent = status.getAttribute('data-copied');
      setTimeout(function () { status.textContent = ''; }, 2400);
    }, function () {});
  });

  /* Footer country / language selector submits as soon as a choice is made.
     Delegated, so it survives section reloads in the editor. */
  document.addEventListener('change', function (e) {
    var el = e.target;
    if (el.matches && el.matches('[data-autosubmit]') && el.form) el.form.submit();
  });

})();

/* Help topic accordions on phones (apple.com/uk/shop/help topic pages) */
(function () {
  function setOpen(section, open) {
    var btn = section.querySelector('[data-acc-toggle]');
    if (!btn) return;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    section.classList.toggle('is-open', open);
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-acc-toggle]');
    if (!btn) return;
    setOpen(btn.closest('.tsec'), btn.getAttribute('aria-expanded') !== 'true');
  });
  function openFromHash() {
    if (!location.hash) return;
    var target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (target && target.classList.contains('tsec')) setOpen(target, true);
  }
  window.addEventListener('hashchange', openFromHash);
  openFromHash();
})();

/* Article tables sit in a keyboard-scrollable region (tabindex=0) so a phone
   reader can reach every column. A table that fits needs no tab stop, so the
   region drops out of the tab order until it actually overflows. */
(function () {
  var boxes = document.querySelectorAll('.post__table');
  if (!boxes.length) return;
  function sync() {
    boxes.forEach(function (box) {
      if (box.scrollWidth > box.clientWidth + 1) box.setAttribute('tabindex', '0');
      else box.removeAttribute('tabindex');
    });
  }
  sync();
  if ('ResizeObserver' in window) {
    var ro = new ResizeObserver(sync);
    boxes.forEach(function (box) { ro.observe(box); });
  } else {
    window.addEventListener('resize', sync);
  }
})();
